(() => {
  'use strict';

  const CONFIG_SELECTOR = '[data-nh-seasonal-pointer-config]';
  const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const IST_OFFSET = '+05:30';
  const INSTANCE_KEY = '__nidharshanaSeasonalPointer';
  const EVENTS_KEY = '__nidharshanaSeasonalPointerEvents';
  const BURST_COLORS = ['#F4D35E', '#FF9F1C', '#8B3A8B', '#FFD166'];
  const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]';
  const POINTER_HOTSPOT_X = 21;
  const POINTER_HOTSPOT_Y = 4;

  function parseIstBoundary(value, isEnd) {
    const normalized = String(value || '').trim();
    if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;

    const [year, month, day] = normalized.split('-').map(Number);
    const calendarDate = new Date(Date.UTC(year, month - 1, day));
    if (
      calendarDate.getUTCFullYear() !== year ||
      calendarDate.getUTCMonth() !== month - 1 ||
      calendarDate.getUTCDate() !== day
    ) return null;

    const time = isEnd ? 'T23:59:59.999' : 'T00:00:00.000';
    const parsed = new Date(`${normalized}${time}${IST_OFFSET}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function diyaMarkup() {
    return `
      <svg class="ns-seasonal-diya-pointer__art" viewBox="0 0 42 50" aria-hidden="true" focusable="false">
        <ellipse class="ns-seasonal-diya-pointer__glow" cx="21" cy="19" rx="12" ry="15" fill="#F4D35E" opacity="0.42"></ellipse>
        <g class="ns-seasonal-diya-pointer__flame">
          <path d="M21 4c5.2 6 6.8 10.4 4.6 14.1-1 1.8-2.7 3-4.6 3-3.2 0-5.8-2.7-5.5-6.1.3-3.3 2.8-6.8 5.5-11Z" fill="#FF9F1C"></path>
          <path d="M21 9.4c2.5 3.3 3.1 5.7 1.9 7.5-.5.8-1.2 1.2-1.9 1.2-1.5 0-2.6-1.2-2.5-2.8.1-1.7 1.2-3.7 2.5-5.9Z" fill="#FFF3B0"></path>
        </g>
        <path d="M6.2 25.2c3.2 10.2 8.5 15.2 15.8 15.2 7.1 0 12.2-5 14-15.2-8.7 3.3-20.4 3.3-29.8 0Z" fill="#8B3A8B" stroke="#4A1552" stroke-width="1.3"></path>
        <path d="M7.4 25.5c8.1 2.4 19.7 2.4 27.4 0-4.1-3.7-9-5.2-13.7-5.2-4.8 0-9.7 1.5-13.7 5.2Z" fill="#F4D35E" stroke="#6D2077" stroke-width="1.15"></path>
        <path d="M12.2 31.2c5.7 2.1 12.2 2.1 18.1 0" fill="none" stroke="#FFF3B0" stroke-width="1.25" stroke-linecap="round" opacity="0.9"></path>
      </svg>`;
  }

  function burstMarkup() {
    return `
      <svg class="ns-seasonal-click-burst__art" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <circle class="ns-seasonal-click-burst__ring" cx="50" cy="50" r="18"></circle>
        <g class="ns-seasonal-click-burst__rays">
          <line x1="50" y1="29" x2="50" y2="7"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(45 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(90 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(135 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(180 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(225 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(270 50 50)"></line>
          <line x1="50" y1="29" x2="50" y2="7" transform="rotate(315 50 50)"></line>
        </g>
        <g class="ns-seasonal-click-burst__dots">
          <circle cx="50" cy="4" r="2.8"></circle>
          <circle cx="83" cy="17" r="2.2"></circle>
          <circle cx="96" cy="50" r="2.8"></circle>
          <circle cx="83" cy="83" r="2.2"></circle>
          <circle cx="50" cy="96" r="2.8"></circle>
          <circle cx="17" cy="83" r="2.2"></circle>
          <circle cx="4" cy="50" r="2.8"></circle>
          <circle cx="17" cy="17" r="2.2"></circle>
        </g>
      </svg>`;
  }

  class SeasonalPointer {
    constructor(config) {
      this.config = config;
      this.cursorEnabled = config.dataset.diyaCursor === 'true';
      this.clickBurstEnabled = config.dataset.clickBurst === 'true';
      this.startDate = parseIstBoundary(config.dataset.start, false);
      this.endDate = parseIstBoundary(config.dataset.end, true);
      this.finePointerMedia = window.matchMedia(FINE_POINTER_QUERY);
      this.motionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.pointer = null;
      this.burstPool = [];
      this.burstIndex = 0;
      this.boundaryTimer = null;
      this.frame = null;
      this.pointerX = -100;
      this.pointerY = -100;
      this.pointerInside = false;
      this.pointerEditable = false;
      this.active = false;
      this.destroyed = false;

      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleEnvironmentChange = this.handleEnvironmentChange.bind(this);
      this.connect();
    }

    connect() {
      if (this.cursorEnabled) this.createPointer();
      if (this.clickBurstEnabled && !this.motionMedia.matches) this.createBurstPool();

      document.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      document.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
      document.addEventListener('click', this.handleClick, { capture: true, passive: true });
      document.addEventListener('visibilitychange', this.handleEnvironmentChange);
      window.addEventListener('blur', this.handleEnvironmentChange);
      window.addEventListener('focus', this.handleEnvironmentChange);
      this.finePointerMedia.addEventListener?.('change', this.handleEnvironmentChange);
      this.motionMedia.addEventListener?.('change', this.handleEnvironmentChange);
      this.sync();
      this.scheduleBoundaryCheck();
    }

    isWithinSchedule() {
      const now = Date.now();
      if (this.startDate && now < this.startDate.getTime()) return false;
      if (this.endDate && now > this.endDate.getTime()) return false;
      return true;
    }

    createPointer() {
      this.pointer = document.createElement('div');
      this.pointer.className = 'ns-seasonal-diya-pointer';
      this.pointer.setAttribute('aria-hidden', 'true');
      this.pointer.innerHTML = diyaMarkup();
      document.body.appendChild(this.pointer);
    }

    createBurstPool() {
      for (let index = 0; index < 6; index += 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ns-seasonal-click-burst';
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.style.setProperty('--ns-seasonal-burst-color', BURST_COLORS[index % BURST_COLORS.length]);
        wrapper.innerHTML = burstMarkup();
        document.body.appendChild(wrapper);

        const art = wrapper.firstElementChild;
        const animation = art.animate([
          { opacity: 0, transform: 'scale(0.2) rotate(-12deg)', offset: 0 },
          { opacity: 1, transform: 'scale(0.62) rotate(-3deg)', offset: 0.12 },
          { opacity: 0.9, transform: 'scale(1) rotate(6deg)', offset: 0.62 },
          { opacity: 0, transform: 'scale(1.22) rotate(10deg)', offset: 1 }
        ], {
          duration: 760,
          easing: 'cubic-bezier(0.18, 0.72, 0.24, 1)',
          fill: 'none'
        });
        animation.cancel();
        this.burstPool.push({ wrapper, animation });
      }
    }

    removeBurstPool() {
      this.burstPool.forEach(({ wrapper, animation }) => {
        animation.cancel();
        wrapper.remove();
      });
      this.burstPool = [];
      this.burstIndex = 0;
    }

    handlePointerMove(event) {
      if (!this.pointer) return;
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      this.pointerInside = true;
      this.pointerEditable = Boolean(event.target.closest?.(EDITABLE_SELECTOR));
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.renderPointer();
      });
    }

    handlePointerLeave() {
      this.pointerInside = false;
      this.renderPointer();
    }

    handleClick(event) {
      if (
        !this.active ||
        !this.clickBurstEnabled ||
        this.motionMedia.matches ||
        event.detail === 0 ||
        typeof event.clientX !== 'number' ||
        typeof event.clientY !== 'number'
      ) return;

      if (!this.burstPool.length) this.createBurstPool();
      const record = this.burstPool[this.burstIndex % this.burstPool.length];
      this.burstIndex += 1;
      record.wrapper.style.transform = `translate3d(${event.clientX - 25}px, ${event.clientY - 25}px, 0)`;
      record.wrapper.style.opacity = '1';
      record.animation.cancel();
      record.animation.play();
    }

    renderPointer() {
      if (!this.pointer) return;
      const visible = this.active && this.finePointerMedia.matches && this.pointerInside && !this.pointerEditable;
      this.pointer.classList.toggle('is-visible', visible);
      if (!visible) return;
      this.pointer.style.transform = `translate3d(${this.pointerX - POINTER_HOTSPOT_X}px, ${this.pointerY - POINTER_HOTSPOT_Y}px, 0)`;
    }

    handleEnvironmentChange() {
      this.sync();
    }

    sync() {
      this.active = !this.destroyed && document.visibilityState === 'visible' && this.isWithinSchedule();
      const cursorActive = this.active && this.cursorEnabled && this.finePointerMedia.matches;
      document.documentElement.classList.toggle('ns-seasonal-diya-cursor-active', cursorActive);
      this.renderPointer();

      if (this.motionMedia.matches && this.burstPool.length) {
        this.removeBurstPool();
      } else if (this.active && this.clickBurstEnabled && !this.motionMedia.matches && !this.burstPool.length) {
        this.createBurstPool();
      }
    }

    scheduleBoundaryCheck() {
      window.clearTimeout(this.boundaryTimer);
      const now = Date.now();
      const boundaries = [];
      if (this.startDate && this.startDate.getTime() > now) boundaries.push(this.startDate.getTime());
      if (this.endDate && this.endDate.getTime() >= now) boundaries.push(this.endDate.getTime() + 1);
      if (!boundaries.length) return;
      const nextBoundary = Math.min(...boundaries);
      this.boundaryTimer = window.setTimeout(() => {
        this.sync();
        this.scheduleBoundaryCheck();
      }, Math.min(Math.max(nextBoundary - now + 50, 1000), 2000000000));
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.boundaryTimer);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      document.removeEventListener('pointermove', this.handlePointerMove);
      document.removeEventListener('pointerleave', this.handlePointerLeave);
      document.removeEventListener('click', this.handleClick, true);
      document.removeEventListener('visibilitychange', this.handleEnvironmentChange);
      window.removeEventListener('blur', this.handleEnvironmentChange);
      window.removeEventListener('focus', this.handleEnvironmentChange);
      this.finePointerMedia.removeEventListener?.('change', this.handleEnvironmentChange);
      this.motionMedia.removeEventListener?.('change', this.handleEnvironmentChange);
      document.documentElement.classList.remove('ns-seasonal-diya-cursor-active');
      this.pointer?.remove();
      this.removeBurstPool();
    }
  }

  function initialize() {
    window[INSTANCE_KEY]?.destroy?.();
    const config = document.querySelector(CONFIG_SELECTOR);
    if (!config || config.dataset.enabled !== 'true') {
      window[INSTANCE_KEY] = null;
      return;
    }
    window[INSTANCE_KEY] = new SeasonalPointer(config);
  }

  if (!window[EVENTS_KEY]) {
    window[EVENTS_KEY] = true;
    document.addEventListener('shopify:section:load', initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
